import cloneDeep from 'lodash/cloneDeep';
import forEach from 'lodash/forEach';
import has from 'lodash/has';
import set from 'lodash/set';
import isNil from 'lodash/isNil';

import { NextData } from 'src/models/Sidekick/SidekickThreads';
import { baseType } from './typeSpecsActions';
import { NestedModuleOrModifier } from './utils';
import {
  hasIncompleteLiquidBrackets,
  isImplicitDataPullInput,
} from 'src/presentation/components/pages/sidekick/threads/components/SidePanel/ExternalActions/ExternalActionInput';
import { selectCache, setCache } from './threadsUiSlice';

type SidekickModuleError = { attr: string; code: string; detail: string };

export const checkMemory = async (
  step: NestedModuleOrModifier,
  extActionMemNames: string[],
  { types, mems }
): Promise<SidekickModuleError[]> => {
  const errors: SidekickModuleError[] = [];

  const spec = step.spec;
  if (!spec) {
    errors.push({
      attr: step.attr!,
      code: 'not_found',
      detail: `Spec is not defined for ${step.mod.name}.`,
    });
    return errors;
  }

  for (const memName in spec.inMemories) {
    const typeName = mems[memName];
    if (!typeName) {
      errors.push({
        attr: step.attr!,
        code: 'not_found',
        detail: `Memory ${memName} does not exist.`,
      });
      continue;
    }

    const typeSpec = types[baseType(typeName)];
    if (typeSpec == null) {
      errors.push({
        attr: step.attr!,
        code: 'not_found',
        detail: `Type ${typeName} is undefined.`,
      });
      continue;
    }
  }

  // external actions
  if ((step.mod as NextData)?.externalAction) {
    const outMem = String(step.mod.controlValues['resultMemoryName'] || '');
    if (outMem == '') {
      errors.push({
        attr: step.attr!,
        code: 'required',
        detail: 'Please enter a Memory name for this Action.',
      });
    }

    // validate external action memory name
    const omnLower = outMem.toLowerCase();

    // This loop does not include the last item, since the last item
    // is the current memory.
    for (let i = 0; i < extActionMemNames.length - 1; i++) {
      const name = extActionMemNames[i];
      // External action result memory names should have
      // case-insensitive uniqueness across all other memories.

      if (name == null) {
        // ignore it 'unexpected null memory name'
        continue;
      }
      if (omnLower === name.toLowerCase()) {
        errors.push({
          attr: step.attr!,
          code: 'duplicate',
          detail: `Please use a unique Memory name as "${outMem}" already exists.`,
        });
        break;
      }
    }

    const inputs = (step.mod as NextData)?.externalAction?.inputs || [];
    for (const input of inputs) {
      if (!Object.getOwnPropertyDescriptor(input, 'value') || input.typeSpec?.type !== 'object') {
        continue;
      }
      const value = input.value;

      if (value === null && !input.typeSpec.null) {
        errors.push({
          attr: step.attr!,
          code: 'required',
          detail: `${outMem} memory is required.`,
        });
        continue;
      }

      try {
        JSON.parse(value as string);
      } catch (e) {
        errors.push({
          attr: step.attr!,
          code: 'invalid',
          detail: 'invalid json format',
        });
        continue;
      }
    }
  }

  if (step.mod.name === 'rule') {
    const ruleSet = step.mod.controlValues['rule'];
    if (ruleSet == null) {
      errors.push({
        attr: step.attr!,
        code: 'required',
        detail: 'Rule set is required.',
      });
    }

    for (const c of ruleSet.conditions) {
      if (!c.if.fields?.length || c.if.fields.includes('')) {
        errors.push({
          attr: step.attr!,
          code: 'required',
          detail: 'Must select valid memory from dropdown.',
        });
      }
      if (ruleSet.then?.pivotFlowID === null || ruleSet.then?.pivotFlowID === -1) {
        errors.push({
          attr: step.attr!,
          code: 'required',
          detail: 'Must select or create a pivot Thread.',
        });
      }
    }
  }

  if (step.mod.name === 'sendCard') {
    const templateInput = step.mod.controlValues?.templateInput;

    if (!templateInput || templateInput?.includes('')) {
      errors.push({
        attr: step.attr!,
        code: 'required',
        detail: 'Missing Memory',
      });
    }
  }

  return errors;
};

export const checkInputs = async (step: NestedModuleOrModifier) => {
  const errors: SidekickModuleError[] = [];

  const extAction = (step.mod as NextData)?.externalAction;
  if (extAction == null) {
    // It's either a modifier or a non-external action module.
    return errors;
  }

  for (const input of extAction.inputs) {
    if (isImplicitDataPullInput(input.name)) {
      continue;
    }

    if (input.typeSpec.null && input.value == null) {
      continue;
    }

    if (!input.typeSpec.null && input.value == null) {
      // The error intentionally does not indicate which input is
      // problematic, so finding a single error is all we need.
      errors.push({
        attr: step.attr!,
        code: 'required',
        detail: 'Please enter the input values for this Action.',
      });
    }

    if (hasIncompleteLiquidBrackets(String(input.value))) {
      errors.push({
        attr: step.attr!,
        code: 'required',
        detail: 'Please complete the input values for this Action.',
      });
    }
  }
  return errors;
};

export const checkLiquid = async (
  step: NestedModuleOrModifier,
  { customerId, types, memoryTypes },
  { gateways, getState, dispatch }
) => {
  const errors: SidekickModuleError[] = [];
  const templates: string[] = [];

  const nextMod = step.mod;

  if (!nextMod.controlValues) {
    errors.push({
      attr: step.attr!,
      code: 'liquid_error',
      detail: 'Control values are not defined.',
    });

    return errors;
  }

  for (const [k, v] of Object.entries(nextMod.controlValues)) {
    if (
      typeof v === 'string' &&
      k.slice(k.length - 5).toLowerCase() !== 'regex' &&
      v?.trim() !== '' &&
      v.includes('{{')
    ) {
      templates.push(v);
    }
  }

  if (templates.length > 0) {
    const result = await withCache(
      'liquid',
      { customerId },
      { templates, types, memoryTypes },
      async (params: unknown, data: unknown) => await gateways.sidekickThreads.validateLiquid(params, data),
      { getState, dispatch }
    );

    if (result?.data.length > 0) {
      forEach(result.data, error => {
        errors.push({
          attr: step.attr!,
          code: 'liquid_error',
          detail: error,
        });
      });
    }
  }

  return errors;
};

const withCache = async (
  key,
  params,
  data,
  callBack: (params: unknown, data: unknown) => Promise<unknown | never>,
  { getState, dispatch }
) => {
  const state = getState();
  let cache = cloneDeep(selectCache(state));
  if (!cache[key]) {
    cache[key] = {};
  }

  const keyInCache = JSON.stringify({ params, data });
  if (has(cache[key], keyInCache)) {
    return cache[key][keyInCache];
  }

  cache[key][keyInCache] = null;
  dispatch(setCache(cache));

  const result = (await callBack(params, data)) as Promise<{ data: string[] }>;
  cache = cloneDeep(selectCache(state));
  cache[key] = set(cache[key], keyInCache, result);
  dispatch(setCache(cache));

  return result;
};

export const checkRegex = async (step: NestedModuleOrModifier, { customerId }, { gateways, dispatch, getState }) => {
  const regExpressions: string[] = [];
  const nextMod = step.mod;
  const errors: SidekickModuleError[] = [];

  if (!nextMod.controlValues) {
    errors.push({
      attr: step.attr!,
      code: 'regex_error',
      detail: 'Control values are not defined.',
    });

    return errors;
  }

  // TODO(johnallen-117) as of right now, lookUpOrder and modules/modifiers
  // with regex trailing characters are the only ones with regex that we need
  // to check. This may change in the future, but these are the only actions
  // that we care about in regards to regex.
  if (nextMod.name.slice(nextMod.name.length - 5).toLowerCase() === 'regex' || nextMod.name === 'lookUpOrder') {
    for (const [k, v] of Object.entries(nextMod.controlValues)) {
      if (k === 'orderNumRegex' || k === 'regexMemory') {
        if (v != null) {
          regExpressions.push(v.toString());
        }
      }
    }
  }

  if (regExpressions.length > 0) {
    const result = await withCache(
      'liquid',
      { customerId },
      { regex: regExpressions },
      async (params: unknown, data: unknown) => await gateways.sidekickThreads.validateRegex(params, data),
      { getState, dispatch }
    );

    if (result?.data.length > 0) {
      forEach(result.data, error => {
        errors.push({
          attr: step.attr!,
          code: 'regex_error',
          detail: error,
        });
      });
    }
  }
  return errors;
};

export const checkTopics = async (step: NestedModuleOrModifier, { getState }) => {
  const nextMod = step.mod;
  const errors: SidekickModuleError[] = [];
  const { values: rawTopics } = getState().topics;

  if (!nextMod.controlValues) {
    return errors;
  }

  if (nextMod.name === 'addTopics') {
    if (!isNil(nextMod.controlValues.useTopicIds)) {
      const topicNamesOrIds = nextMod.controlValues?.tags?.split(',').map(tag => tag.trim());

      const enabledTopics = rawTopics.filter(
        topic => topicNamesOrIds.includes(nextMod.controlValues.useTopicIds ? topic.id : topic.name) && !topic.disabled
      );
      if (enabledTopics.length !== topicNamesOrIds.length) {
        const disabledTopics = topicNamesOrIds
          .filter(
            nameOrId =>
              !enabledTopics.find(topic =>
                nextMod.controlValues.useTopicIds ? topic.id === nameOrId : topic.name === nameOrId
              )
          )
          .map(
            nameOrId =>
              rawTopics.find(topic => (nextMod.controlValues.useTopicIds ? topic.id : topic.name) === nameOrId)?.name ||
              nameOrId
          );

        errors.push({
          attr: step.attr!,
          code: 'not_exist',
          detail: `Topics ${disabledTopics.join(', ')} do not exist or disabled.`,
        });
      }
    }
  }

  return errors;
};
